import { useQuery } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { apiFetch } from '@/api/client';
import { handleResponse } from '@/api/handleResponse';
// Type-only — never pulls the `.server` runtime module into the bundle.
import type { User, Organisation } from '@/models/user.server';

export type { User, Organisation };

/** The Auth0 access/ID token namespaced claim that carries the backend user id. */
export const ETCHO_USER_ID_CLAIM = 'https://pro.etcho.io/etchoUserId';

/** Browser port of `getUser` from app/models/user.server.ts. */
export async function fetchUser(userId: string) {
    const response = await apiFetch(`/users/${userId}`, { method: 'GET' });
    return handleResponse<User>(response, `GET /users/${userId}`);
}

/**
 * Current authenticated user. Replaces the old root-loader `getUser` +
 * `useOptionalUser`. The backend user id comes from the Auth0 token claim.
 */
export function useCurrentUser() {
    const { user, isAuthenticated } = useAuth0();
    const userId = user?.[ETCHO_USER_ID_CLAIM] as string | undefined;

    return useQuery({
        queryKey: ['user', userId],
        enabled: isAuthenticated && !!userId,
        queryFn: async () => {
            const { data, error } = await fetchUser(userId as string);
            if (error) throw new Error(error);
            return data;
        },
    });
}
